import { SimpleGrid, Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { useLiveQuery } from 'dexie-react-hooks'
import { enqueueSnackbar } from 'notistack'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useNavigate } from 'react-router'

import { useDb } from '@/plugins/db/Db'
import { convertBytesToUnit, convertUnitToBytes, SavedDraft } from '@/utils/utils'

import Loading from '../Loading'
import OptionsMenu from '../OptionsMenu'
import Snackbar from '../Snackbar'
import { jsonSize, MAX_DRAFTS_SIZE, MAX_DRAFTS_UNIT } from './CreateItem'
import CreationsCard from './CreationsCard'

const ChunkSize = 8

const Drafts: React.FC = () => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const { db } = useDb()
  const [limit, setLimit] = useState<number>(4)
  const [offset, setOffset] = useState<number>(0)

  const [draftsSizeInBytes, setDraftsSizeInBytes] = useState<number>(0)
  const [draftSizeInMB, setDraftSizeInMB] = useState<string>('0 MB')
  const [draftList, setDraftList] = useState<SavedDraft[]>([])
  const [isDeletingDraft, setIsDeletingDraft] = useState<[string]>([''])

  const limitedDrafts: SavedDraft[] | undefined = useLiveQuery(async () => {
    if (!db) return []
    try {
      setIsLoading(true)
      const drafts =
        (await db
          ?.table('drafts')
          .orderBy('timestamp')
          .reverse()
          .limit(limit)
          .offset(offset)
          .toArray()) ?? []
      setIsLoading(false)
      setDraftList(prevLoadedDrafts => {
        const newDrafts = drafts.filter(
          draft => !prevLoadedDrafts.some(prevDraft => prevDraft.id === draft.id)
        )
        return [...prevLoadedDrafts, ...newDrafts]
      })

      return drafts
    } catch (err) {
      console.error('Error fetching drafts', err)
      enqueueSnackbar(
        <Snackbar
          title={t('components.studio.fetchDraftError', 'Error fetching drafts')}
          value={t('components.studio.draftErrorDetails', '{{error}}', {
            error: err,
          })}
          icon={<></>}
        />
      )
      setIsLoading(false)
    }
    return []
  }, [db, limit, offset])

  const countDrafts: number | undefined = useLiveQuery(async () => {
    if (!db) return 0

    try {
      const count = (await db?.table('drafts').count()) ?? 0
      return count
    } catch (err) {
      enqueueSnackbar(
        <Snackbar
          title={t('components.studio.fetchDraftError', 'Error fetching drafts')}
          value={t('components.studio.draftErrorDetails', '{{error}}', {
            error: err,
          })}
          icon={<></>}
        />
      )
    }
    return 0
  }, [db])

  // get drafts size from indexed db dexie
  useEffect(() => {
    const getDraftsSize = async () => {
      // check if db exists and if drafts table exists
      if (db) {
        try {
          await db
            .table('drafts')
            .toArray()
            .then(drafts => {
              const size = drafts.reduce((acc, draft) => {
                const imageSize = draft.draftJson.asset.size
                return acc + imageSize + jsonSize
              }, 0)
              setDraftsSizeInBytes(size)
              setDraftSizeInMB(convertBytesToUnit(size))
            })
        } catch (err) {
          console.error('Error fetching drafts:', err)
          enqueueSnackbar(
            <Snackbar
              title={t('components.studio.fetchDraftError', 'Error fetching drafts')}
              value={t('components.studio.draftErrorDetails', '{{error}}', {
                error: err,
              })}
              icon={<></>}
            />
          )
        }
      }
    }
    getDraftsSize()
  }, [db, countDrafts])

  const draftSize = useCallback((draft: any) => {
    const imageSize = draft.draftJson.asset.size
    return convertBytesToUnit(imageSize + jsonSize)
  }, [])

  const onDeleteDraft = useCallback(
    async (draft: SavedDraft) => {
      setIsDeletingDraft([draft.id])
      if (db) {
        try {
          await db.table('drafts').where('id').equals(draft.id).delete()
        } catch (err) {
          console.error('Error deleting draft:', err)
          enqueueSnackbar(
            <Snackbar
              title={t('components.studio.deleteDraftError', 'Error deleting draft')}
              value={t('components.studio.draftErrorDetails', '{{error}}', {
                error: err,
              })}
              icon={<></>}
            />
          )
        }
      }
      setIsDeletingDraft([''])
    },
    [db]
  )

  return (
    <Stack>
      <Title order={3}>{t('components.studio.drafts', 'Drafts')}</Title>
      <Text>
        {
          t(
            'components.studio.draftSubtitle',
            `Work-in-progress items you still need to complete and publish. You've used {{draftSizeInMB}} ({{percentageTaken}}%) of your browser’s {{maxDrafts}} limit. {{remainingSpace}} remaining.`,
            {
              draftSizeInMB,
              maxDrafts: `${MAX_DRAFTS_SIZE}${MAX_DRAFTS_UNIT}`,
              percentageTaken: Math.round(
                (draftsSizeInBytes / convertUnitToBytes(MAX_DRAFTS_SIZE, MAX_DRAFTS_UNIT)) * 100
              ),
              remainingSpace: convertBytesToUnit(
                Math.max(
                  convertUnitToBytes(MAX_DRAFTS_SIZE, MAX_DRAFTS_UNIT) - draftsSizeInBytes,
                  0
                )
              ),
            }
          ) as string
        }
      </Text>
      {isLoading && <Loading />}
      {limitedDrafts && limitedDrafts.length > 0 && !isLoading && (
        <InfiniteScroll
          style={{ overflow: 'hidden' }}
          dataLength={limitedDrafts.length}
          next={() => {
            setOffset(prev => prev + draftList.length)
            setLimit(prev => prev + ChunkSize)
          }}
          hasMore={(countDrafts || limit) > limit}
          loader={<Loading />}
        >
          <SimpleGrid>
            {draftList?.map((draft: SavedDraft) => {
              const image = // draft?.draftJson?.asset File into URL
                draft?.draftJson?.asset && URL.createObjectURL(new Blob([draft?.draftJson?.asset]))
              return (
                <Stack key={draft?.id} style={{ position: 'relative' }}>
                  <CreationsCard
                    draftData={draft?.draftJson}
                    key={draft?.id}
                    title={draft?.draftJson?.name}
                    description={draft?.draftJson?.description}
                    image={image}
                    timestamp={draft?.timestamp}
                    filesize={draftSize(draft)}
                  />
                  {isDeletingDraft && isDeletingDraft.includes(draft.id) ? (
                    <Stack
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        padding: '5px 10px',
                        borderRadius: '12px',
                        backgroundColor: theme.colors.background2[0],
                        gap: '10px',
                      }}
                    >
                      <Text>{t('components.studio.deletingDraft', 'Deleting Draft...')}</Text>
                      <Loading size="20px" />
                    </Stack>
                  ) : (
                    <OptionsMenu style={{ position: 'absolute', top: '5px', right: '5px' }}>
                      <a
                        onClick={() => {
                          navigate('./createItem', {
                            state: { draftData: draft?.draftJson },
                            replace: true,
                          })
                        }}
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                      >
                        {t('components.studio.continueDraft', 'Continue Draft')}
                      </a>
                      <a
                        onClick={() => {
                          onDeleteDraft(draft)
                        }}
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                      >
                        {t('components.studio.deleteDraft', 'Delete Draft')}
                      </a>
                    </OptionsMenu>
                  )}
                </Stack>
              )
            })}
          </SimpleGrid>
        </InfiniteScroll>
      )}
    </Stack>
  )
}

export default Drafts
